import { COUPON_CODE_PREFIX_REGEX } from '@lib/models/aop'

// A QR's mcId is always exactly this many characters, in the first field.
export const MC_ID_LENGTH = 6
// A coupon code is always exactly this many characters.
export const COUPON_CODE_LENGTH = 9

/**
 * Whether `code` starts with a coupon code (COUPON_CODE_LENGTH alphanumeric
 * characters) cleanly bounded by a real field separator ('|' or 'FF99') or
 * the end of the string. Coupons can come bare (just the code) or extended
 * with extra fields after it, same as a QR. Anchoring to that exact
 * boundary means a coupon (or a QR's mcId) that happens to contain the
 * literal substring 'FF99' or '|' inside its own characters can't be
 * mistaken for a real separator.
 */
export function isCouponCode(code: string): boolean {
  return COUPON_CODE_PREFIX_REGEX.test(code)
}

/**
 * Whether `code` is a QR (identification) code: not a coupon, and made of
 * several fields separated by '|' or 'FF99'. Anything that's neither is
 * treated as a (likely malformed) coupon attempt — its own format check
 * bails out safely, which is safer than defaulting an unrecognized scan
 * into a real login attempt.
 */
export function isQrCode(code: string): boolean {
  return !isCouponCode(code) && (code.includes('FF99') || code.includes('|'))
}

/** Extracts the coupon code candidate: the first COUPON_CODE_LENGTH characters. */
export function extractCouponCode(code: string): string {
  return code.slice(0, COUPON_CODE_LENGTH)
}

/** Extracts a QR's mcId: the first MC_ID_LENGTH characters. */
export function extractMcId(code: string): string {
  return code.slice(0, MC_ID_LENGTH)
}

const FIELD_SEPARATORS = ['|', 'FF99']

/**
 * Splits a scanned coupon into its code and, when the scan is an identified
 * coupon (code + extra fields), the mcId carried in the 2nd field. The mcId
 * is taken as-is: it is NOT related to the coupon code, so it is never
 * derived from it. A bare coupon (or a manual entry) has no mcId.
 */
export function parseCouponScan(raw: string): { coupon: string; mcId?: string } {
  const coupon = extractCouponCode(raw)
  const rest = raw.slice(COUPON_CODE_LENGTH)
  const separator = FIELD_SEPARATORS.find((sep) => rest.startsWith(sep))
  if (!separator) return { coupon }

  const field = rest.slice(separator.length)
  const mcId = extractMcId(field)
  const afterMcId = field.slice(MC_ID_LENGTH)
  // the 2nd field must be exactly an mcId: followed by the end of the scan or a real separator.
  // That rejects short fields (they would swallow part of the next separator) and longer ones,
  // whichever separator the scan uses
  const endsAtBoundary =
    afterMcId === '' || FIELD_SEPARATORS.some((sep) => afterMcId.startsWith(sep))
  if (mcId.length !== MC_ID_LENGTH || !endsAtBoundary) return { coupon }
  return { coupon, mcId }
}

const QR_SEPARATOR_REGEX = /FF99|\|/

/** QR de autenticación con 4 parámetros (el último es el token IM) */
export const qrHasToken = (qr: string): boolean => {
  const parts = qr.split(QR_SEPARATOR_REGEX)
  // >= 4 because the token (base64url JWT) could contain 'FF99'
  return parts.length >= 4 && !!parts[3]
}
